import { useEffect, useState, useMemo, useRef } from "react";
import {
    ArrowUp, ArrowDown, Plus, Trash, FloppyDisk, ArrowCounterClockwise,
    Scales, Warning, ArrowLeft, Sparkle, Archive, ArrowsClockwise,
    PencilSimple, CheckCircle, FileText, GitDiff, Copy, DownloadSimple,
    UploadSimple, Eye, EyeSlash,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import CompareWithDefaultDialog from "../lib/CompareWithDefaultDialog";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
    DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";

const AVAILABLE_PLACEHOLDERS = [
    "{{client_name}}", "{{client_address}}",
    "{{company_legal_name}}", "{{company_registered_office}}",
    "{{proposal_date}}",
    "{{notices_name}}", "{{notices_email}}", "{{notices_phone}}",
];

const EMPTY_DRAFT = {
    name: "",
    description: "",
    preamble: "",
    clauses: [{ title: "Agreement", body: "Initial clause body. Edit as needed." }],
    payment_terms: ["50% Advance along with Purchase Order."],
};

export default function TermsConditions() {
    const { can } = useAuth();
    const [view, setView] = useState({ kind: "list" });   // {kind:'list'} or {kind:'edit', id} or {kind:'create'}
    const [sets, setSets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [includeArchived, setIncludeArchived] = useState(false);
    // 2026-02 · Import-from-Word dialog state — Super-Admin uploads a
    // .docx and gets a brand-new T&C set added to the list.
    const [importOpen, setImportOpen] = useState(false);
    const [importName, setImportName] = useState("");
    const [importDescription, setImportDescription] = useState("");
    const [importFile, setImportFile] = useState(null);
    const [importBusy, setImportBusy] = useState(false);
    const importFileRef = useRef(null);

    const loadList = async () => {
        try {
            const url = includeArchived
                ? "/billing/proposal-tnc/list?include_archived=1"
                : "/billing/proposal-tnc/list";
            const { data } = await api.get(url);
            setSets(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load T&C sets"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadList(); }, [includeArchived]);

    // Iter 66.4 — Sales has read-only access. canWrite gates write affordances
    // (new set, edit, make default, archive/restore). Server enforces
    // template:write on every mutation endpoint independently.
    const canWrite = can("template:write");

    const doImport = async () => {
        const trimmed = (importName || "").trim();
        if (!trimmed) { toast.error("Name is required"); return; }
        if (!importFile) { toast.error("Please pick a .docx file"); return; }
        if (!/\.docx$/i.test(importFile.name || "")) {
            toast.error("File must be a .docx (Word) document");
            return;
        }
        const form = new FormData();
        form.append("name", trimmed);
        form.append("description", importDescription || "");
        form.append("file", importFile);
        setImportBusy(true);
        try {
            const { data } = await api.post("/billing/proposal-tnc/import.docx", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(
                `Imported "${data.name}" · ${data.clauses_count} clauses · ${data.payment_terms_count} payment terms`
            );
            setImportOpen(false);
            setImportName("");
            setImportDescription("");
            setImportFile(null);
            if (importFileRef.current) importFileRef.current.value = "";
            loadList();
        } catch (err) {
            toast.error(formatApiError(err, "Import failed"));
        } finally {
            setImportBusy(false);
        }
    };

    if (view.kind === "view") {
        // Iter 2026-02 · Admin / super-admin "View" — opens the same
        // read-only viewer that Sales / Viewer roles get for their Edit
        // fallback. Distinct from `edit` so canWrite users can inspect a
        // T&C set without landing in a mutation surface.
        return (
            <TncReadOnlyViewer
                tncId={view.id}
                onBack={() => setView({ kind: "list" })}
            />
        );
    }

    if (view.kind === "edit" || view.kind === "create") {
        if (!canWrite) {
            if (view.kind === "create") {
                // Read-only users cannot create. Bounce to list defensively.
                setView({ kind: "list" });
                return null;
            }
            // Iter 66.6 — Sales / Viewer get a compact read-only viewer rather
            // than being bounced. They need to be able to *read* the clauses
            // + payment terms before selecting a T&C set on the proposal.
            return (
                <TncReadOnlyViewer
                    tncId={view.id}
                    onBack={() => setView({ kind: "list" })}
                />
            );
        }
        return (
            <TncEditor
                tncId={view.kind === "edit" ? view.id : null}
                onBack={() => { setView({ kind: "list" }); loadList(); }}
                onOpen={(id) => setView({ kind: "edit", id })}
            />
        );
    }

    return (
        <div data-testid="tnc-page">
            <PageHeader
                eyebrow={canWrite ? "Administration" : "Administration · Read-only"}
                title="Terms & Conditions"
                description={
                    canWrite
                        ? "Manage multiple T&C sets — pick one as default for new proposals; switch per-proposal as needed. Placeholders like {{client_name}} resolve at print time."
                        : "Browse the org's T&C sets. The default set is auto-attached to new proposals; you can switch the active set per-proposal from the proposal editor. Editing T&C language is admin-only."
                }
                actions={
                    <div className="flex items-center gap-2">
                        <label className="text-[10px] inline-flex items-center gap-1.5 text-slate-500 cursor-pointer" data-testid="tnc-include-archived-toggle">
                            <input
                                type="checkbox"
                                checked={includeArchived}
                                onChange={(e) => setIncludeArchived(e.target.checked)}
                                className="w-3.5 h-3.5 accent-slate-700"
                            />
                            Show archived
                        </label>
                        {canWrite && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setImportOpen(true)}
                                className="gap-1.5"
                                data-testid="tnc-import-docx-btn"
                            >
                                <UploadSimple size={14} weight="bold" /> Import from Word
                            </Button>
                        )}
                        {canWrite && (
                            <Button
                                onClick={() => setView({ kind: "create" })}
                                className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                                data-testid="tnc-new-set-btn"
                            >
                                <Plus size={14} weight="bold" /> New T&C set
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-8 max-w-5xl space-y-3">
                {loading ? (
                    <div className="text-sm text-slate-500">Loading…</div>
                ) : sets.length === 0 ? (
                    <EmptyState
                        icon={<Scales size={36} weight="thin" />}
                        title="No T&C sets configured"
                        description='Click "New T&C set" to create your first one.'
                    />
                ) : (
                    <div className="space-y-3" data-testid="tnc-list">
                        {sets.map((s) => (
                            <TncRow
                                key={s.id}
                                tnc={s}
                                onOpen={() => setView({ kind: "edit", id: s.id })}
                                onView={() => setView({ kind: "view", id: s.id })}
                                onChanged={loadList}
                                canWrite={canWrite}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* 2026-02 · Import from Word (.docx) dialog. Creates a new
                T&C set from an uploaded document — never overwrites
                existing sets. */}
            <Dialog open={importOpen} onOpenChange={(open) => {
                if (importBusy) return;
                setImportOpen(open);
                if (!open) {
                    setImportName("");
                    setImportDescription("");
                    setImportFile(null);
                    if (importFileRef.current) importFileRef.current.value = "";
                }
            }}>
                <DialogContent data-testid="tnc-import-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <UploadSimple size={18} weight="fill" className="text-slate-700" />
                            Import T&C from Word
                        </DialogTitle>
                        <DialogDescription>
                            Upload a .docx file to add it as a brand-new T&C set.
                            Existing sets are never overwritten. For best fidelity,
                            start by exporting an existing set, editing it in Word,
                            and importing the edited copy back.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 pt-1">
                        <div>
                            <Label className="text-xs font-semibold">New T&C set name</Label>
                            <Input
                                autoFocus
                                value={importName}
                                onChange={(e) => setImportName(e.target.value)}
                                placeholder='e.g. "Client X · Negotiated T&C"'
                                className="mt-1.5"
                                data-testid="tnc-import-name-input"
                                maxLength={120}
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">
                                Description <span className="font-normal text-slate-400">(optional)</span>
                            </Label>
                            <Input
                                value={importDescription}
                                onChange={(e) => setImportDescription(e.target.value)}
                                placeholder="Short summary shown on the T&C list"
                                className="mt-1.5"
                                data-testid="tnc-import-description-input"
                                maxLength={300}
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Word file (.docx)</Label>
                            <Input
                                type="file"
                                ref={importFileRef}
                                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                                className="mt-1.5"
                                data-testid="tnc-import-file-input"
                            />
                            <p className="text-[10px] text-slate-500 mt-1">
                                Use Word Heading 1 to mark clause titles. Sections titled
                                &quot;Preamble&quot; and &quot;Payment Terms&quot; are parsed specially. Max 5 MiB.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setImportOpen(false)}
                            disabled={importBusy}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={doImport}
                            disabled={importBusy || !importName.trim() || !importFile}
                            className="bg-slate-900 hover:bg-slate-800 text-white"
                            data-testid="tnc-import-confirm-btn"
                        >
                            {importBusy ? "Importing…" : "Import as new set"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function TncRow({ tnc, onOpen, onView, onChanged, canWrite }) {
    const isArchived = tnc.archived;
    const isDefault = tnc.is_default;
    const isHidden = !!tnc.hidden_from_dropdown;

    const makeDefault = async (e) => {
        e.stopPropagation();
        try {
            await api.post(`/billing/proposal-tnc/${tnc.id}/make-default`);
            toast.success(`"${tnc.name}" is now the default`);
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed"));
        }
    };

    const archive = async (e) => {
        e.stopPropagation();
        if (!window.confirm(`Archive "${tnc.name}"? Existing proposals using it still print correctly; new proposals can't pick it.`)) return;
        try {
            await api.post(`/billing/proposal-tnc/${tnc.id}/archive`);
            toast.success("Archived");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed"));
        }
    };

    const restore = async (e) => {
        e.stopPropagation();
        try {
            await api.post(`/billing/proposal-tnc/${tnc.id}/restore`);
            toast.success("Restored");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed"));
        }
    };

    const toggleHidden = async (e) => {
        e.stopPropagation();
        try {
            const { data } = await api.post(`/billing/proposal-tnc/${tnc.id}/toggle-hidden`);
            toast.success(
                data.hidden_from_dropdown
                    ? `"${tnc.name}" hidden from the proposal picker`
                    : `"${tnc.name}" visible in the proposal picker`
            );
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed"));
        }
    };

    const exportDocx = async (e) => {
        e.stopPropagation();
        // Bearer-token auth means a plain <a> link can't carry the
        // Authorization header — fetch as a blob via axios and trigger
        // the download manually.
        try {
            const resp = await api.get(
                `/billing/proposal-tnc/${tnc.id}/export.docx`,
                { responseType: "blob" }
            );
            const blob = new Blob([resp.data], {
                type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });
            const url = URL.createObjectURL(blob);
            const safeName = (tnc.name || "tnc").replace(/[^A-Za-z0-9._-]+/g, "_") || "tnc";
            const a = document.createElement("a");
            a.href = url;
            a.download = `TnC_${safeName}.docx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            toast.error(formatApiError(err, "Export failed"));
        }
    };

    return (
        <div
            className={`reedos-card p-5 transition-colors cursor-pointer hover:border-slate-400 ${isArchived ? "opacity-60" : ""}`}
            onClick={() => (canWrite && onView ? onView() : onOpen())}
            data-testid={`tnc-row-${tnc.id}`}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-bold text-slate-900 text-base truncate">{tnc.name}</h3>
                        {isDefault && (
                            <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold border bg-emerald-50 text-emerald-800 border-emerald-200" data-testid={`tnc-default-badge-${tnc.id}`}>
                                <CheckCircle size={10} weight="fill" /> Default
                            </span>
                        )}
                        {isArchived && (
                            <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold border bg-amber-50 text-amber-800 border-amber-200">
                                <Archive size={10} weight="fill" /> Archived
                            </span>
                        )}
                        {isHidden && !isArchived && (
                            <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold border bg-slate-100 text-slate-700 border-slate-300" data-testid={`tnc-hidden-badge-${tnc.id}`}>
                                <EyeSlash size={10} weight="fill" /> Hidden from picker
                            </span>
                        )}
                    </div>
                    {tnc.description && (
                        <p className="text-xs text-slate-600 mt-1">{tnc.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                        <span className="inline-flex items-center gap-1"><FileText size={11} /> {tnc.clauses_count} clause{tnc.clauses_count !== 1 ? "s" : ""}</span>
                        <span className="inline-flex items-center gap-1"><Sparkle size={11} /> {tnc.payment_terms_count} payment term{tnc.payment_terms_count !== 1 ? "s" : ""}</span>
                        <span className="font-mono text-[10px] text-slate-400">{tnc.id}</span>
                    </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {!canWrite && (
                        <span className="text-[11px] text-slate-400 italic" data-testid={`tnc-readonly-tag-${tnc.id}`}>
                            View
                        </span>
                    )}
                    {canWrite && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onView?.(); }}
                            className="gap-1"
                            title="Read-only view — see all clauses and payment terms without entering the editor"
                            data-testid={`tnc-view-btn-${tnc.id}`}
                        >
                            <Eye size={12} /> View
                        </Button>
                    )}
                    {canWrite && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onOpen(); }}
                            className="gap-1"
                            data-testid={`tnc-edit-btn-${tnc.id}`}
                        >
                            <PencilSimple size={12} /> Edit
                        </Button>
                    )}
                    {/* Export is available to all viewers — reading is safe. */}
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={exportDocx}
                        className="text-slate-600 hover:text-slate-900"
                        title="Download as Word (.docx)"
                        data-testid={`tnc-export-btn-${tnc.id}`}
                    >
                        <DownloadSimple size={12} /> Word
                    </Button>
                    {canWrite && !isArchived && !isDefault && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={toggleHidden}
                            className={isHidden ? "text-emerald-700 hover:bg-emerald-50" : "text-slate-500 hover:text-slate-900"}
                            title={isHidden ? "Show in proposal picker" : "Hide from proposal picker"}
                            data-testid={`tnc-toggle-hidden-btn-${tnc.id}`}
                        >
                            {isHidden ? <Eye size={12} /> : <EyeSlash size={12} />}
                            {isHidden ? "Show" : "Hide"}
                        </Button>
                    )}
                    {canWrite && !isArchived && !isDefault && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={makeDefault}
                            className="text-emerald-700 hover:bg-emerald-50"
                            data-testid={`tnc-make-default-btn-${tnc.id}`}
                        >
                            <CheckCircle size={12} /> Make default
                        </Button>
                    )}
                    {canWrite && !isArchived && !isDefault && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={archive}
                            className="text-slate-400 hover:text-rose-600"
                            data-testid={`tnc-archive-btn-${tnc.id}`}
                        >
                            <Archive size={12} />
                        </Button>
                    )}
                    {canWrite && isArchived && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={restore}
                            className="text-slate-700 hover:text-slate-900"
                            data-testid={`tnc-restore-btn-${tnc.id}`}
                        >
                            <ArrowsClockwise size={12} /> Restore
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

function TncReadOnlyViewer({ tncId, onBack }) {
    // Iter 66.6 — Lightweight read-only viewer for Sales / Viewer roles.
    // No mutations, no editor state, no draft-tracking. Renders the T&C set
    // as a printable-looking document so Sales can read clauses + payment
    // terms before picking a set on a proposal.
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/billing/proposal-tnc/${tncId}`);
                if (!cancelled) setDoc(data);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load T&C set"));
                if (!cancelled) onBack();
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [tncId, onBack]);

    if (loading) return <div className="text-sm text-slate-500 p-5">Loading…</div>;
    if (!doc) return null;

    const isDefault = doc.is_default;
    const isArchived = doc.archived;

    return (
        <div data-testid="tnc-readonly-page">
            <PageHeader
                title={doc.name || "Untitled set"}
                description={
                    <span className="inline-flex items-center gap-2 text-xs flex-wrap">
                        <FileText size={14} />
                        {doc.clauses?.length || 0} clauses · {doc.payment_terms?.length || 0} payment terms
                        {isDefault && (
                            <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold border bg-emerald-50 text-emerald-800 border-emerald-200">
                                <CheckCircle size={10} weight="fill" /> Default
                            </span>
                        )}
                        {isArchived && (
                            <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold border bg-amber-50 text-amber-800 border-amber-200">
                                <Archive size={10} weight="fill" /> Archived
                            </span>
                        )}
                        <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold border bg-slate-50 text-slate-700 border-slate-200">
                            View only
                        </span>
                    </span>
                }
                actions={
                    <Button variant="outline" size="sm" onClick={onBack} className="gap-1.5" data-testid="tnc-readonly-back">
                        <ArrowLeft size={13} /> Back to list
                    </Button>
                }
            />
            <div className="max-w-4xl mx-auto space-y-5">
                {doc.description && (
                    <div className="reedos-card p-5">
                        <Label className="reedos-eyebrow">Description</Label>
                        <p className="text-sm text-slate-700 mt-1">{doc.description}</p>
                    </div>
                )}

                {doc.preamble && (
                    <div className="reedos-card p-5" data-testid="tnc-readonly-preamble">
                        <Label className="reedos-eyebrow">Preamble</Label>
                        <p className="text-[13px] text-slate-800 mt-2 whitespace-pre-wrap leading-relaxed">
                            {doc.preamble}
                        </p>
                    </div>
                )}

                {(doc.payment_terms?.length || 0) > 0 && (
                    <div className="reedos-card p-5" data-testid="tnc-readonly-payment-terms">
                        <Label className="reedos-eyebrow">Payment Terms &amp; Other Conditions</Label>
                        <ol className="space-y-2 mt-3 list-decimal pl-5">
                            {doc.payment_terms.map((t, idx) => (
                                <li key={idx} className="text-[13px] text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid={`tnc-readonly-payment-term-${idx}`}>
                                    {t}
                                </li>
                            ))}
                        </ol>
                    </div>
                )}

                {(doc.clauses?.length || 0) > 0 && (
                    <div className="space-y-3" data-testid="tnc-readonly-clauses-list">
                        {doc.clauses.map((c, idx) => (
                            <div key={idx} className="reedos-card p-5" data-testid={`tnc-readonly-clause-${idx}`}>
                                <div className="flex items-start gap-3">
                                    <span className="font-mono text-xs font-bold text-slate-900 bg-slate-100 rounded px-2 py-0.5 mt-0.5 shrink-0">
                                        {idx + 1}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-display font-semibold text-slate-900 text-sm">
                                            {c.title}
                                        </h4>
                                        <p className="text-[13px] text-slate-700 mt-1.5 whitespace-pre-wrap leading-relaxed">
                                            {c.body}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function TncEditor({ tncId, onBack, onOpen }) {
    const isCreate = !tncId;
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [preamble, setPreamble] = useState("");
    const [clauses, setClauses] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(!isCreate);
    const [saving, setSaving] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [compareOpen, setCompareOpen] = useState(false);
    const [dirty, setDirty] = useState(false);
    // Iter 2026-02 · "Save As" clone dialog (user directive 2026-02-16).
    // Lets operators duplicate an existing T&C set into a fresh one
    // without touching the original, so they can iterate on a copy.
    const [saveAsOpen, setSaveAsOpen] = useState(false);
    const [saveAsName, setSaveAsName] = useState("");
    const [saveAsBusy, setSaveAsBusy] = useState(false);

    const load = async () => {
        if (isCreate) {
            const d = EMPTY_DRAFT;
            setName(d.name); setDescription(d.description); setPreamble(d.preamble);
            setClauses(d.clauses.map((c) => ({ ...c })));
            setPaymentTerms([...d.payment_terms]);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get(`/billing/proposal-tnc/${tncId}`);
            setName(data.name || "");
            setDescription(data.description || "");
            setPreamble(data.preamble || "");
            setClauses((data.clauses || []).map((c) => ({ title: c.title, body: c.body })));
            setPaymentTerms(data.payment_terms || []);
            setMeta({ updated_at: data.updated_at, updated_by: data.updated_by, is_default: data.is_default, archived: data.archived, id: data.id });
            setDirty(false);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load T&C set"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-line */ }, [tncId]);

    const markDirty = () => setDirty(true);

    const updateClause = (idx, key, value) => {
        setClauses((p) => p.map((c, i) => (i === idx ? { ...c, [key]: value } : c)));
        markDirty();
    };
    const moveClause = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= clauses.length) return;
        setClauses((p) => { const n = [...p]; [n[idx], n[target]] = [n[target], n[idx]]; return n; });
        markDirty();
    };
    const addClause = () => { setClauses((p) => [...p, { title: "New clause", body: "" }]); markDirty(); };
    const removeClause = (idx) => { setClauses((p) => p.filter((_, i) => i !== idx)); markDirty(); };

    const updatePaymentTerm = (idx, value) => { setPaymentTerms((p) => p.map((t, i) => i === idx ? value : t)); markDirty(); };
    const movePaymentTerm = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= paymentTerms.length) return;
        setPaymentTerms((p) => { const n = [...p]; [n[idx], n[target]] = [n[target], n[idx]]; return n; });
        markDirty();
    };
    const addPaymentTerm = () => { setPaymentTerms((p) => [...p, "New term"]); markDirty(); };
    const removePaymentTerm = (idx) => { setPaymentTerms((p) => p.filter((_, i) => i !== idx)); markDirty(); };

    const save = async () => {
        if (!name.trim()) { toast.error("Name is required"); return; }
        const invalid = clauses.some((c) => !c.title.trim() || !c.body.trim());
        if (invalid || clauses.length === 0) {
            toast.error("Every clause needs a title and body. At least one clause is required.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                description: description.trim(),
                preamble,
                clauses: clauses.map((c, i) => ({ order: i + 1, title: c.title.trim(), body: c.body })),
                payment_terms: paymentTerms.map((t) => t.trim()).filter((t) => t),
            };
            if (isCreate) {
                const { data } = await api.post("/billing/proposal-tnc", payload);
                toast.success(`Created "${data.name}"`);
                onBack();
            } else {
                await api.put(`/billing/proposal-tnc/${tncId}`, payload);
                toast.success("Saved");
                await load();
            }
        } catch (err) {
            toast.error(formatApiError(err, "Failed to save"));
        } finally {
            setSaving(false);
        }
    };

    // Iter 2026-02 · "Save As" — clone the existing set on the server
    // (backend copies preamble / clauses / payment_terms verbatim),
    // then navigate to the new set's editor so the operator can start
    // editing immediately. Local unsaved edits are NOT included in the
    // clone — the dialog clearly says the clone is made from the LAST
    // SAVED state.
    const saveAs = async () => {
        const trimmed = saveAsName.trim();
        if (!trimmed) { toast.error("Name is required"); return; }
        setSaveAsBusy(true);
        try {
            const { data } = await api.post(`/billing/proposal-tnc/${tncId}/duplicate`, {
                name: trimmed,
            });
            toast.success(`Created "${data.name}" — you can now edit the copy`);
            setSaveAsOpen(false);
            setSaveAsName("");
            // Navigate to the new set's editor by handing the id back to
            // the parent (list page → editor pattern the file already uses).
            if (typeof onOpen === "function") onOpen(data.id);
            else onBack();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to duplicate"));
        } finally {
            setSaveAsBusy(false);
        }
    };

    const doReset = async () => {
        setSaving(true);
        try {
            await api.post("/billing/proposal-tnc/reset-to-default");
            toast.success("Default T&C set restored to original 16 clauses + 9 terms");
            setResetOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed"));
        } finally {
            setSaving(false);
        }
    };

    const insertPlaceholder = (idx, placeholder, target) => {
        const el = document.getElementById(target === "clause" ? `tnc-body-${idx}` : `tnc-pt-${idx}`);
        if (!el) return;
        const start = el.selectionStart ?? 0;
        const end = el.selectionEnd ?? 0;
        const current = target === "clause" ? clauses[idx].body : paymentTerms[idx];
        const next = (current || "").slice(0, start) + placeholder + (current || "").slice(end);
        if (target === "clause") updateClause(idx, "body", next);
        else updatePaymentTerm(idx, next);
        setTimeout(() => {
            el.focus();
            const pos = start + placeholder.length;
            el.setSelectionRange(pos, pos);
        }, 0);
    };

    const isMaster = meta?.id === "master";

    return (
        <div data-testid="tnc-editor-page">
            <PageHeader
                eyebrow={
                    <button onClick={onBack} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="tnc-back-btn">
                        <ArrowLeft size={12} /> All T&C sets
                    </button>
                }
                title={isCreate ? "New T&C set" : (name || "Editing…")}
                description={
                    !isCreate && meta?.updated_at
                        ? `${clauses.length} clauses · ${paymentTerms.length} payment terms · last updated ${formatDateTime(meta.updated_at)}`
                        : "Define a reusable Terms & Conditions set."
                }
                actions={
                    <div className="flex items-center gap-2">
                        {!isCreate && !isMaster && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCompareOpen(true)}
                                className="gap-1.5"
                                data-testid="tnc-compare-btn"
                            >
                                <GitDiff size={14} weight="fill" /> Compare with Default
                            </Button>
                        )}
                        {!isCreate && isMaster && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setResetOpen(true)}
                                className="gap-1.5"
                                data-testid="tnc-reset-btn"
                            >
                                <ArrowCounterClockwise size={14} /> Restore defaults
                            </Button>
                        )}
                        {!isCreate && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    // Prefill "Copy of <name>" — operators
                                    // almost always want to keep the parent
                                    // name and tweak the suffix.
                                    setSaveAsName(`Copy of ${name}`.slice(0, 200));
                                    setSaveAsOpen(true);
                                }}
                                className="gap-1.5"
                                data-testid="tnc-save-as-btn"
                            >
                                <Copy size={14} weight="fill" /> Save as new
                            </Button>
                        )}
                        <Button
                            onClick={save}
                            disabled={saving || (!isCreate && !dirty)}
                            className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                            data-testid="tnc-save-btn"
                        >
                            <FloppyDisk size={14} weight="fill" />
                            {saving ? "Saving…" : isCreate ? "Create T&C set" : (dirty ? "Save changes" : "No changes")}
                        </Button>
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-8 max-w-4xl space-y-6">
                {loading ? (
                    <div className="text-sm text-slate-500">Loading…</div>
                ) : (
                    <>
                        <div className="reedos-card p-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="tnc-meta-card">
                            <div>
                                <Label className="text-xs font-semibold">Name</Label>
                                <Input
                                    value={name}
                                    onChange={(e) => { setName(e.target.value); markDirty(); }}
                                    className="mt-1.5"
                                    placeholder='e.g. "Standard MFBO Terms"'
                                    data-testid="tnc-name-input"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Description <span className="font-normal text-slate-400">(optional)</span></Label>
                                <Input
                                    value={description}
                                    onChange={(e) => { setDescription(e.target.value); markDirty(); }}
                                    className="mt-1.5"
                                    placeholder='e.g. "For mutual fund back-office engagements"'
                                    data-testid="tnc-description-input"
                                />
                            </div>
                            <div className="sm:col-span-2 text-[10px] text-slate-500">
                                <span className="font-semibold text-slate-700">Available placeholders:</span>{" "}
                                {AVAILABLE_PLACEHOLDERS.map((p) => (
                                    <code key={p} className="inline-block bg-slate-100 rounded px-1 py-0.5 mr-1 font-mono text-[10px]">{p}</code>
                                ))}
                            </div>
                        </div>

                        <div className="reedos-card p-5" data-testid="tnc-preamble-card">
                            <Label className="reedos-eyebrow">Preamble</Label>
                            <p className="text-[11px] text-slate-500 mt-0.5 mb-2">
                                Opening paragraph printed before clause 1.
                            </p>
                            <Textarea
                                value={preamble}
                                onChange={(e) => { setPreamble(e.target.value); markDirty(); }}
                                rows={8}
                                className="mt-1 font-mono text-[12px] leading-relaxed"
                                data-testid="tnc-preamble-textarea"
                            />
                        </div>

                        <div className="reedos-card p-5" data-testid="tnc-payment-terms-card">
                            <div className="flex items-start justify-between gap-2 mb-1">
                                <div>
                                    <Label className="reedos-eyebrow">Payment Terms &amp; Other Conditions</Label>
                                    <p className="text-[11px] text-slate-500 mt-0.5">
                                        Numbered list printed in the "Payment Terms" section. Placeholders resolve at print time.
                                    </p>
                                </div>
                                <Button type="button" variant="outline" size="sm" onClick={addPaymentTerm} className="gap-1 shrink-0" data-testid="tnc-payment-term-add">
                                    <Plus size={12} weight="bold" /> Add term
                                </Button>
                            </div>
                            {paymentTerms.length === 0 ? (
                                <div className="text-center text-xs text-slate-500 py-6">
                                    No payment terms configured. Click "Add term" to start.
                                </div>
                            ) : (
                                <ol className="space-y-2 mt-3" data-testid="tnc-payment-terms-list">
                                    {paymentTerms.map((t, idx) => (
                                        <li key={idx} className="flex items-start gap-2" data-testid={`tnc-payment-term-${idx}`}>
                                            <span className="mt-2 font-mono text-[11px] font-bold text-slate-700 bg-slate-100 rounded px-1.5 py-0.5 min-w-[22px] text-center">
                                                {idx + 1}
                                            </span>
                                            <Textarea
                                                id={`tnc-pt-${idx}`}
                                                value={t}
                                                onChange={(e) => updatePaymentTerm(idx, e.target.value)}
                                                rows={Math.max(1, Math.min(4, (t || "").length > 90 ? 2 : 1))}
                                                className="flex-1 font-mono text-[12px] leading-relaxed"
                                                data-testid={`tnc-payment-term-input-${idx}`}
                                            />
                                            <div className="flex flex-col gap-0.5 pt-1">
                                                <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-slate-900" disabled={idx === 0} onClick={() => movePaymentTerm(idx, -1)}>
                                                    <ArrowUp size={12} weight="bold" />
                                                </Button>
                                                <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-slate-900" disabled={idx === paymentTerms.length - 1} onClick={() => movePaymentTerm(idx, +1)}>
                                                    <ArrowDown size={12} weight="bold" />
                                                </Button>
                                            </div>
                                            <Button type="button" variant="ghost" size="icon" className="mt-1 text-slate-400 hover:text-rose-600" onClick={() => removePaymentTerm(idx)}>
                                                <Trash size={13} />
                                            </Button>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>

                        <div className="space-y-3" data-testid="tnc-clauses-list">
                            {clauses.map((c, idx) => (
                                <div key={idx} className="reedos-card p-5" data-testid={`tnc-clause-${idx}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="flex flex-col items-center pt-1">
                                            <span className="font-mono text-xs font-bold text-slate-900 bg-slate-100 rounded px-2 py-0.5">
                                                {idx + 1}
                                            </span>
                                            <div className="flex flex-col mt-2 gap-0.5">
                                                <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-slate-900" disabled={idx === 0} onClick={() => moveClause(idx, -1)}>
                                                    <ArrowUp size={12} weight="bold" />
                                                </Button>
                                                <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-slate-900" disabled={idx === clauses.length - 1} onClick={() => moveClause(idx, +1)}>
                                                    <ArrowDown size={12} weight="bold" />
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0 space-y-2">
                                            <div className="flex items-start gap-2">
                                                <Input value={c.title} onChange={(e) => updateClause(idx, "title", e.target.value)} className="font-display font-semibold" data-testid={`tnc-clause-title-${idx}`} placeholder="Clause title" />
                                                <Button type="button" variant="ghost" size="icon" className="text-slate-400 hover:text-rose-600 shrink-0" onClick={() => removeClause(idx)}>
                                                    <Trash size={14} />
                                                </Button>
                                            </div>
                                            <Textarea
                                                id={`tnc-body-${idx}`}
                                                value={c.body}
                                                onChange={(e) => updateClause(idx, "body", e.target.value)}
                                                rows={Math.min(18, Math.max(5, (c.body || "").split("\n").length + 2))}
                                                className="font-mono text-[12px] leading-relaxed"
                                                data-testid={`tnc-clause-body-${idx}`}
                                            />
                                            <div className="flex flex-wrap gap-1">
                                                {AVAILABLE_PLACEHOLDERS.map((p) => (
                                                    <button key={p} type="button" onClick={() => insertPlaceholder(idx, p, "clause")} className="text-[10px] font-mono bg-slate-50 hover:bg-slate-900 hover:text-white border border-slate-200 rounded px-1.5 py-0.5 transition-colors">
                                                        {p}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div className="flex justify-center">
                                <Button type="button" variant="outline" onClick={addClause} className="gap-1.5">
                                    <Plus size={14} weight="bold" /> Add new clause
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <CompareWithDefaultDialog
                open={compareOpen}
                onOpenChange={setCompareOpen}
                currentName={name || "(unnamed)"}
                currentPreamble={preamble}
                currentClauses={clauses}
                currentPaymentTerms={paymentTerms}
            />

            <Dialog open={resetOpen} onOpenChange={setResetOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <Warning size={18} weight="fill" className="text-amber-600" />
                            Restore Default set?
                        </DialogTitle>
                        <DialogDescription>
                            This overwrites the "Default" set's clauses + payment terms with the original 16-clause REEDOS template.
                            All custom edits to the Default set will be lost. Other T&C sets are not affected.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
                        <Button onClick={doReset} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white">
                            {saving ? "Restoring…" : "Yes, restore defaults"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter 2026-02 · "Save As" — quick clone dialog. Copies the
                LAST SAVED preamble / clauses / payment terms into a
                brand-new set with the requested name. Unsaved local
                edits are NOT included in the clone (banner tells the
                operator this explicitly). */}
            <Dialog open={saveAsOpen} onOpenChange={setSaveAsOpen}>
                <DialogContent data-testid="tnc-save-as-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <Copy size={18} weight="fill" className="text-slate-700" />
                            Save as new T&C set
                        </DialogTitle>
                        <DialogDescription>
                            Creates a fresh T&C set with the same preamble, clauses and payment terms as <span className="font-medium">{name || "this set"}</span>. The original is untouched.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 pt-1">
                        <div>
                            <Label className="text-xs font-semibold">New name</Label>
                            <Input
                                autoFocus
                                value={saveAsName}
                                onChange={(e) => setSaveAsName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && !saveAsBusy) saveAs(); }}
                                placeholder="e.g. Enterprise T&C · v2"
                                className="mt-1.5"
                                data-testid="tnc-save-as-name-input"
                                maxLength={200}
                            />
                        </div>
                        {dirty && (
                            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="tnc-save-as-dirty-warning">
                                <Warning size={12} weight="fill" className="inline mr-1 -mt-0.5" />
                                You have unsaved edits. The copy will be made from the <span className="font-semibold">last saved</span> version — save first if you want your latest changes in the copy too.
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSaveAsOpen(false)} disabled={saveAsBusy}>Cancel</Button>
                        <Button
                            onClick={saveAs}
                            disabled={saveAsBusy || !saveAsName.trim()}
                            className="bg-slate-900 hover:bg-slate-800 text-white"
                            data-testid="tnc-save-as-confirm-btn"
                        >
                            {saveAsBusy ? "Creating…" : "Create copy & open"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
